import { useState } from 'react'
import { AnchoredOverlay } from '@orbind/ui'
import {
  EMOJIS_RAPIDOS,
  partesComLinks,
  partesComMencao,
  percentualDaOpcao,
  type EnqueteApurada,
  podeApagarMensagem,
  podeModerarCanal,
  type MensagemDoCanal,
  type PapelNoCanal,
} from '@orbind/core'
import { aoClicarLinkExterno } from '../../lib/link-externo.ts'
import { avatarDoAutor, urlDoAnexo } from './useChat.ts'

/**
 * Uma mensagem na conversa.
 *
 * Visual = o do protótipo (`ch-msgrow` / `ch-bubble` em pages/channels.css).
 * O que o protótipo não tinha — foto do autor, citação, reações, anexos, menu —
 * é composto com as mesmas peças (`ch-avatar`, `ch-more__item`, `ch-iconbtn`),
 * sem estilo visual novo.
 */

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase()
}

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

/**
 * Texto comum do balão com os endereços clicáveis (pedido 2026-10-08).
 *
 * Elementos React, nunca HTML montado: o texto é de outra pessoa. O clique abre
 * no navegador padrão — no desktop, `aoClicarLinkExterno` desvia da webview.
 */
function TextoComLinks({ texto }: { texto: string }) {
  return (
    <>
      {partesComLinks(texto).map((parte, i) =>
        parte.href ? (
          <a
            key={i}
            className="ch-link"
            href={parte.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={aoClicarLinkExterno}
            onAuxClick={aoClicarLinkExterno}
          >
            {parte.texto}
          </a>
        ) : (
          parte.texto
        ),
      )}
    </>
  )
}

export type AcoesDaMensagem = {
  onResponder: (m: MensagemDoCanal) => void
  onResponderEmParticular: (m: MensagemDoCanal) => void
  onCopiar: (m: MensagemDoCanal) => void
  onReagir: (m: MensagemDoCanal, emoji: string) => void
  onVotar: (m: MensagemDoCanal, opcao: number) => void
  onEncaminhar: (m: MensagemDoCanal) => void
  onApagar: (m: MensagemDoCanal) => void
  onFixar: (m: MensagemDoCanal) => void
  onFavoritar: (m: MensagemDoCanal) => void
  onIrParaCitada: (messageId: string) => void
}

export function MessageItem({
  mensagem,
  euMemberId,
  meuPapel,
  primeiraDoBloco,
  conversaDireta,
  nomesMencionaveis = [],
  acoes,
}: {
  mensagem: MensagemDoCanal
  euMemberId: string
  meuPapel: PapelNoCanal
  /** Primeira de uma sequência do mesmo autor: só ela mostra foto e nome. */
  primeiraDoBloco: boolean
  /** Na conversa direta o protótipo não repete o nome do autor. */
  conversaDireta: boolean
  /** Nomes de quem participa: `@Nome` deles sai destacado no balão. */
  nomesMencionaveis?: readonly string[]
  acoes: AcoesDaMensagem
}) {
  const [menuAberto, setMenuAberto] = useState<HTMLElement | null>(null)
  const [emojisAbertos, setEmojisAbertos] = useState<HTMLElement | null>(null)

  const minha = mensagem.autor?.memberId === euMemberId
  const apagada = mensagem.apagadaEm !== null
  const foto = mensagem.autor ? avatarDoAutor(mensagem.autor) : null

  const podeApagar = podeApagarMensagem(mensagem, euMemberId, meuPapel)
  const podeFixar = podeModerarCanal(meuPapel)
  // Conversar no particular não faz sentido comigo mesmo nem com o sistema.
  const podeChamarNoPrivado = mensagem.autor !== null && !minha

  const fechar = () => setMenuAberto(null)

  // Hora NA LINHA do texto, como no mensageiro de referência (pedido
  // 2026-09-14): a hora não abre linha própria embaixo, o que engrossava todo
  // balão. Só quando o balão termina em texto — depois de foto ou documento a
  // hora continua embaixo do anexo.
  const metaNaLinha = apagada || (mensagem.corpo !== '' && mensagem.anexos.length === 0)
  const destacarMencao = mensagem.mencionaMe && !minha && !apagada
  const conteudoDaMeta = (
    <>
      {mensagem.fixada ? <i className="fas fa-thumbtack" title="Fixada" aria-hidden /> : null}
      {mensagem.favorita ? <i className="fas fa-star" title="Favorita" aria-hidden /> : null}
      {hora(mensagem.enviadaEm)}
    </>
  )
  // A vaga é uma cópia invisível da hora no fim do texto: reserva exatamente a
  // largura dela. Se couber, a hora fica na última linha; se não, a vaga quebra
  // e a hora desce sozinha — sem medir nada em JavaScript.
  const vagaDaMeta = metaNaLinha ? (
    <span className="ch-bubble__vaga" aria-hidden>
      {conteudoDaMeta}
    </span>
  ) : null

  return (
    <div
      id={`msg-${mensagem.id}`}
      className={`ch-msgrow ch-msgrow--acoes${minha ? ' ch-msgrow--mine' : ''}`}
      onContextMenu={(e) => {
        if (apagada) return
        e.preventDefault()
        setMenuAberto(e.currentTarget)
      }}
    >
      {/* Foto do autor (pedido "primeiro de tudo"). Nas minhas mensagens o
          protótipo alinha à direita sem foto; nas seguidas do mesmo autor a
          vaga continua ocupada para o balão não pular. */}
      {!minha ? (
        <span className="ch-avatarwrap ch-msgrow__foto" aria-hidden={!primeiraDoBloco}>
          {primeiraDoBloco && mensagem.autor ? (
            <span className="ch-avatar" title={mensagem.autor.name}>
              {foto ? <img className="ch-avatar__img" src={foto} alt="" /> : iniciais(mensagem.autor.name)}
            </span>
          ) : null}
        </span>
      ) : null}

      <div className="ch-msgrow__col">
        <div
          className={`ch-bubble${minha ? ' ch-bubble--mine' : ''}${metaNaLinha ? ' ch-bubble--meta-na-linha' : ''}${
            destacarMencao ? ' ch-bubble--mencao' : ''
          }${!apagada && mensagem.anexos.some((a) => a.tipo === 'enquete') ? ' ch-bubble--enquete' : ''}`}
        >
          {primeiraDoBloco && !minha && !conversaDireta && mensagem.autor ? (
            <span className="ch-bubble__author">{mensagem.autor.name}</span>
          ) : null}

          {/* Menção a mim: além do fundo, um rótulo — status nunca só por cor. */}
          {destacarMencao ? (
            <span className="ch-bubble__meta ch-bubble__meta--topo ch-bubble__mencao">
              <i className="fas fa-at" aria-hidden /> Mencionou você
            </span>
          ) : null}

          {mensagem.encaminhada && !apagada ? (
            <span className="ch-bubble__meta ch-bubble__meta--topo">
              <i className="fas fa-share" aria-hidden /> Encaminhada
            </span>
          ) : null}

          {/* Citação: acionar leva até a mensagem original. */}
          {mensagem.respondendo && !apagada ? (
            <button
              type="button"
              className="ch-quote"
              onClick={() => acoes.onIrParaCitada(mensagem.respondendo!.id)}
            >
              <span className="ch-bubble__author">{mensagem.respondendo.autor ?? 'Alguém'}</span>
              <span className="ch-row__preview">
                {mensagem.respondendo.apagada ? 'Mensagem apagada' : mensagem.respondendo.trecho}
              </span>
            </button>
          ) : null}

          {apagada ? (
            <span className="ch-bubble__text ch-bubble__text--apagada">
              <i className="fas fa-ban" aria-hidden /> Esta mensagem foi apagada
              {vagaDaMeta}
            </span>
          ) : (
            <>
              {mensagem.corpo ? (
                <span className="ch-bubble__text">
                  {partesComMencao(mensagem.corpo, nomesMencionaveis).map((parte, i) =>
                    parte.mencao ? (
                      <span key={i} className="ch-mention">
                        {parte.texto}
                      </span>
                    ) : (
                      <TextoComLinks key={i} texto={parte.texto} />
                    ),
                  )}
                  {vagaDaMeta}
                </span>
              ) : null}
              {mensagem.anexos.map((anexo) =>
                anexo.tipo === 'enquete' && anexo.enquete ? (
                  <Enquete key={anexo.id} enquete={anexo.enquete} onVotar={(opcao) => acoes.onVotar(mensagem, opcao)} />
                ) : (
                  <Anexo key={anexo.id} anexo={anexo} />
                ),
              )}
            </>
          )}

          <span className="ch-bubble__meta">{conteudoDaMeta}</span>
        </div>

        {mensagem.reacoes.length > 0 ? (
          <div className="ch-reactions">
            {mensagem.reacoes.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`ch-reaction${r.euReagi ? ' ch-reaction--mine' : ''}`}
                title={r.quem.join(', ')}
                onClick={() => acoes.onReagir(mensagem, r.emoji)}
              >
                <span aria-hidden>{r.emoji}</span> {r.total}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {!apagada ? (
        <button
          type="button"
          className="ch-iconbtn ch-msgrow__menu"
          aria-label="Ações da mensagem"
          aria-haspopup="menu"
          onClick={(e) => setMenuAberto(e.currentTarget)}
        >
          <i className="fas fa-chevron-down" aria-hidden />
        </button>
      ) : null}

      {menuAberto ? (
        <AnchoredOverlay
          anchor={menuAberto}
          onClose={fechar}
          prefer={minha ? 'esquerda' : 'direita'}
          role="menu"
          aria-label="Ações da mensagem"
          className="ch-menu"
        >
          {/* Barra rápida: seis com um toque, e o `+` abre o resto. */}
          <div className="ch-menu__emojis">
            {EMOJIS_RAPIDOS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="ch-iconbtn"
                aria-label={`Reagir com ${emoji}`}
                onClick={() => {
                  acoes.onReagir(mensagem, emoji)
                  fechar()
                }}
              >
                {emoji}
              </button>
            ))}
            <button
              type="button"
              className="ch-iconbtn"
              aria-label="Mais emojis"
              onClick={(e) => {
                setEmojisAbertos(e.currentTarget)
                fechar()
              }}
            >
              <i className="fas fa-plus" aria-hidden />
            </button>
          </div>

          <ItemDoMenu icone="fas fa-reply" rotulo="Responder" onClick={() => { acoes.onResponder(mensagem); fechar() }} />
          {podeChamarNoPrivado ? (
            <ItemDoMenu
              icone="fas fa-user"
              rotulo="Responder em particular"
              onClick={() => { acoes.onResponderEmParticular(mensagem); fechar() }}
            />
          ) : null}
          <ItemDoMenu icone="fas fa-copy" rotulo="Copiar" onClick={() => { acoes.onCopiar(mensagem); fechar() }} />
          <ItemDoMenu icone="fas fa-share" rotulo="Encaminhar" onClick={() => { acoes.onEncaminhar(mensagem); fechar() }} />
          <ItemDoMenu
            icone="fas fa-star"
            rotulo={mensagem.favorita ? 'Desfavoritar' : 'Favoritar'}
            onClick={() => { acoes.onFavoritar(mensagem); fechar() }}
          />
          {/* Indisponível aparece APAGADO, nunca escondido. */}
          <ItemDoMenu
            icone="fas fa-thumbtack"
            rotulo={mensagem.fixada ? 'Desafixar' : 'Fixar'}
            desabilitado={!podeFixar}
            dica="Só quem modera fixa mensagens"
            onClick={() => { acoes.onFixar(mensagem); fechar() }}
          />
          <ItemDoMenu
            icone="fas fa-flag"
            rotulo="Denunciar"
            desabilitado
            dica="Em breve: a denúncia vai ao dono do workspace"
            onClick={() => undefined}
          />
          <ItemDoMenu
            icone="fas fa-trash"
            rotulo="Apagar"
            desabilitado={!podeApagar}
            dica="Só o autor ou quem modera apaga"
            onClick={() => { acoes.onApagar(mensagem); fechar() }}
          />
        </AnchoredOverlay>
      ) : null}

      {emojisAbertos ? (
        <AnchoredOverlay
          anchor={emojisAbertos}
          onClose={() => setEmojisAbertos(null)}
          prefer={minha ? 'esquerda' : 'direita'}
          role="dialog"
          aria-label="Escolher emoji"
          className="ch-menu"
        >
          <div className="ch-menu__grade">
            {EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="ch-iconbtn"
                onClick={() => {
                  acoes.onReagir(mensagem, emoji)
                  setEmojisAbertos(null)
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </AnchoredOverlay>
      ) : null}
    </div>
  )
}

/** Item de menu = o `ch-more__item` do protótipo. */
function ItemDoMenu({
  icone,
  rotulo,
  onClick,
  desabilitado = false,
  dica,
}: {
  icone: string
  rotulo: string
  onClick: () => void
  desabilitado?: boolean
  dica?: string
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="ch-more__item"
      disabled={desabilitado}
      title={desabilitado ? dica : undefined}
      onClick={onClick}
    >
      <i className={icone} aria-hidden />
      {rotulo}
    </button>
  )
}

/**
 * Enquete votável, no formato do mensageiro de referência: pergunta, instrução,
 * uma linha por opção com o marcador (redondo = uma resposta, quadrado = várias),
 * a contagem à direita e a barra de proporção embaixo. Tocar numa opção vota;
 * tocar de novo desmarca. "Ver votos" abre quem escolheu o quê.
 */
function Enquete({ enquete, onVotar }: { enquete: EnqueteApurada; onVotar: (opcao: number) => void }) {
  const [verVotos, setVerVotos] = useState(false)
  const maior = Math.max(0, ...enquete.opcoes.map((o) => o.total))

  return (
    <div className="ch-enquete">
      <span className="ch-enquete__pergunta">{enquete.pergunta}</span>
      <span className="ch-enquete__instrucao">
        <i className={enquete.multipla ? 'fas fa-check-double' : 'fas fa-check'} aria-hidden />
        {enquete.multipla ? 'Selecione uma ou mais opções' : 'Selecione uma opção'}
      </span>

      <div className="ch-enquete__opcoes" role={enquete.multipla ? 'group' : 'radiogroup'} aria-label={enquete.pergunta}>
        {enquete.opcoes.map((opcao) => {
          const pct = percentualDaOpcao(opcao.total, enquete.votantes)
          return (
            <button
              key={opcao.indice}
              type="button"
              role={enquete.multipla ? 'checkbox' : 'radio'}
              aria-checked={opcao.euVotei}
              className={`ch-enquete__opcao${opcao.euVotei ? ' ch-enquete__opcao--minha' : ''}`}
              title={opcao.quem.length > 0 ? opcao.quem.join(', ') : 'Ninguém votou ainda'}
              onClick={() => onVotar(opcao.indice)}
            >
              <span
                className={`ch-enquete__marca${enquete.multipla ? ' ch-enquete__marca--quadrada' : ''}`}
                aria-hidden
              >
                {opcao.euVotei ? <i className="fas fa-check" /> : null}
              </span>
              <span className="ch-enquete__corpo">
                <span className="ch-enquete__linha">
                  <span className="ch-enquete__texto">{opcao.texto}</span>
                  <span className="ch-enquete__total">{opcao.total}</span>
                </span>
                <span className="ch-enquete__barra" aria-hidden>
                  <span
                    className={`ch-enquete__preenchido${opcao.total > 0 && opcao.total === maior ? ' ch-enquete__preenchido--lider' : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <button
        type="button"
        className="ch-enquete__vervotos"
        disabled={enquete.votantes === 0}
        aria-expanded={verVotos}
        onClick={() => setVerVotos((v) => !v)}
      >
        {enquete.votantes === 0
          ? 'Nenhum voto ainda'
          : verVotos
          ? 'Ocultar votos'
          : `Ver votos · ${enquete.votantes} ${enquete.votantes === 1 ? 'pessoa' : 'pessoas'}`}
      </button>

      {verVotos && enquete.votantes > 0 ? (
        <div className="ch-enquete__votos">
          {enquete.opcoes
            .filter((o) => o.total > 0)
            .map((o) => (
              <div key={o.indice} className="ch-enquete__grupo">
                <span className="ch-enquete__grupo-titulo">
                  {o.texto} <span className="ch-enquete__total">{o.total}</span>
                </span>
                <span className="ch-enquete__nomes">{o.quem.join(', ')}</span>
              </div>
            ))}
        </div>
      ) : null}
    </div>
  )
}

function Anexo({ anexo }: { anexo: MensagemDoCanal['anexos'][number] }) {
  if (anexo.tipo === 'imagem' || anexo.tipo === 'video') {
    const ehVideo = (anexo.mimeType ?? '').startsWith('video/')
    return ehVideo ? (
      <video className="ch-attach__media" src={urlDoAnexo(anexo.id)} controls />
    ) : (
      <a href={urlDoAnexo(anexo.id)} target="_blank" rel="noreferrer">
        <img className="ch-attach__media" src={urlDoAnexo(anexo.id)} alt={anexo.fileName ?? ''} />
      </a>
    )
  }

  if (anexo.tipo === 'gif') {
    const p = anexo.payload ?? {}
    return <img className="ch-attach__media ch-attach__gif" src={String(p.url ?? '')} alt={String(p.titulo ?? 'GIF')} />
  }

  if (anexo.tipo === 'audio') {
    return <audio className="ch-attach__media" src={urlDoAnexo(anexo.id)} controls />
  }

  // Contato, enquete, evento e documento: todos no formato de linha de contato
  // do protótipo (`ch-contact`), trocando só ícone e textos.
  const p = anexo.payload ?? {}
  const linha = (icone: string, nome: string, sub: string, extra?: React.ReactNode) => (
    <span className="ch-contact ch-attach">
      <span className="ch-contact__avatar">
        <i className={icone} aria-hidden />
      </span>
      <span className="ch-contact__main">
        <span className="ch-contact__name">{nome}</span>
        {sub ? <span className="ch-contact__sub">{sub}</span> : null}
        {extra}
      </span>
    </span>
  )

  if (anexo.tipo === 'contato') return linha('fas fa-user', String(p.name ?? 'Contato'), String(p.email ?? ''))

  if (anexo.tipo === 'enquete') {
    const opcoes = Array.isArray(p.opcoes) ? (p.opcoes as string[]) : []
    return linha(
      'fas fa-list-ul',
      String(p.pergunta ?? 'Enquete'),
      '',
      opcoes.map((o) => (
        <span key={o} className="ch-contact__sub">
          • {o}
        </span>
      )),
    )
  }

  if (anexo.tipo === 'evento') {
    return linha('fas fa-calendar-day', String(p.titulo ?? 'Evento'), String(p.quando ?? ''))
  }

  return (
    <a className="ch-attach__link" href={urlDoAnexo(anexo.id)} target="_blank" rel="noreferrer">
      {linha(
        'fas fa-file-alt',
        anexo.fileName ?? 'Arquivo',
        anexo.sizeBytes ? `${(anexo.sizeBytes / 1024).toFixed(0)} KB` : '',
      )}
    </a>
  )
}

/**
 * Teclado de emoji: os emojis do sistema, lista curta — uma dependência de 1 MB
 * só para isso não se paga.
 */
const EMOJIS = [
  '😀','😂','🥹','😊','😍','😘','😎','🤔','😐','😴','🤒','🤯','🥳','😭','😡','🤝',
  '👍','👎','👏','🙏','💪','🫶','👀','🔥','⭐','❤️','💔','✅','❌','⚠️','🎉','🚀',
  '📌','📎','📅','⏰','💡','🐛','☕','🍕','🎯','📈','📉','💰','🏆','🙌','🤞','✨',
]
